import { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 (or `from`) to `to` once the element scrolls into view.
 * Returns [ref, displayValue].
 */
export default function useCountUp(to, { from = 0, duration = 1600, suffix = "" } = {}) {
  const [val, setVal] = useState(from);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const target = Number(to);
    if (Number.isNaN(target)) return;

    const animate = () => {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        setVal(Math.round(from + (target - from) * eased));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            animate();
            io.disconnect();
          }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, from, duration]);

  return [ref, val.toLocaleString() + suffix];
}
